import React, { useContext, useEffect, useState, useRef } from 'react';
import { Box, Grid, Typography, IconButton, useTheme, useMediaQuery, Menu, MenuItem, Container } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import { useNavigate } from 'react-router-dom';
import UserApi from '../../../../rest-api/user-api';
import { useSnackbar } from '../../SnackbarProvider';
import DownloadUtils from '../../../../utils/download-utils';
import WhiteboardCard from './WhiteboardCard';
import CustomisedHandoutCard from '../customise-mode/CustomisedHandoutCard';
import { FeedbackCTA } from '../../feedback-cta';
import GradientBanner from '../../../UIControls/GradientBanner';
import PinkGradientButtonWithArrow from '../../../UIControls/PinkGradientButtonWithArrow';
import { archiveWhiteboard, cleanupPatientWhiteboardsFromLibrary } from '../../../helper/whiteboardUtils';
import { archiveCustomisedHandout } from '../../../helper/customisedHandoutUtils';

const MyLibrary = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { userDataStore, whiteboardStore, authStore, imageStore, customisedHandoutStore } = useContext(StoreContext);

    // State variables
    const [whiteboards, setWhiteboards] = useState([]);
    const [customisedHandouts, setCustomisedHandouts] = useState([]);
    const [combinedItems, setCombinedItems] = useState([]); // Combined whiteboards and customised handouts
    const [filteredWhiteboards, setFilteredWhiteboards] = useState([]);
    const [page, setPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [sortBy, setSortBy] = useState('RECENT');
    const [imageMap, setImageMap] = useState({});
    const [sortDirection, setSortDirection] = useState('desc'); // 'desc' = Newest first
    const [hasSortedRecently, setHasSortedRecently] = useState(false);
    const [filterType, setFilterType] = useState('customisedHandouts'); // 'all', 'whiteboards', 'customisedHandouts'

    const whiteboardsPerPage = 6;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const showSnackbar = useSnackbar();

    const paginatedWhiteboards = filteredWhiteboards.slice(page * whiteboardsPerPage, (page + 1) * whiteboardsPerPage);

    // **Prevent multiple fetches of images**
    const fetchedImages = useRef(new Set());

    // Cleanup patient whiteboards from My Library on mount
    useEffect(() => {
        const cleanup = async () => {
            if (!whiteboardStore.isInitialized) return;

            try {
                const result = await cleanupPatientWhiteboardsFromLibrary(userDataStore, whiteboardStore, authStore);
                if (result.cleaned) {
                    console.log(`Cleaned up ${result.count} patient whiteboards from My Library`);
                }
            } catch (err) {
                console.error('Failed to cleanup patient whiteboards:', err);
            }
        };

        cleanup();
    }, [whiteboardStore.isInitialized, userDataStore, whiteboardStore, authStore]);

    useEffect(() => {
        // Only update your local state after the store is ready
        if (!whiteboardStore.isInitialized) return;

        let whiteboardsData = Object.values(whiteboardStore.whiteboardData);
        console.log('Updating Whiteboards State');

        const myLibraryIds = userDataStore.userData.whiteboards || [];
        // Only show active records
        whiteboardsData = whiteboardsData
            .filter((wb) => wb.whiteboardStatus !== 'archived' && myLibraryIds.includes(wb.whiteboardId))
            .sort((a, b) => b.lastUpdated - a.lastUpdated);
        setWhiteboards(whiteboardsData);
    }, [whiteboardStore.whiteboardData, whiteboardStore.isInitialized]);

    useEffect(() => {
        // Load customised handouts from store - EXACT COPY of whiteboard logic
        if (!customisedHandoutStore?.isInitialized) return;

        let customisedHandoutsData = Object.values(customisedHandoutStore.customisedHandoutData);
        console.log('[MY LIBRARY] Updating Customised Handouts State');

        const myLibraryIds = userDataStore.userData.customisedHandouts || [];

        // Only show active records that are in My Library (no patientId)
        customisedHandoutsData = customisedHandoutsData
            .filter((ch) => ch.customisedStatus === 'active' && !ch.patientId && myLibraryIds.includes(ch.customisedId))
            .sort((a, b) => b.lastUpdated - a.lastUpdated);

        setCustomisedHandouts(customisedHandoutsData);
    }, [
        customisedHandoutStore?.customisedHandoutData,
        customisedHandoutStore?.isInitialized,
        userDataStore.userData.customisedHandouts,
    ]);

    // Combine whiteboards and customised handouts
    useEffect(() => {
        const whiteboardFavouriteIds = userDataStore.userData.whiteboardsFavourites || [];
        const customisedHandoutFavouriteIds = userDataStore.userData.customisedHandoutsFavourites || [];

        let combined = [
            ...whiteboards.map((wb) => ({ ...wb, type: 'whiteboard' })),
            ...customisedHandouts.map((ch) => ({ ...ch, type: 'customisedHandout' })),
        ];

        console.log('Combined items (before sort and filter):', combined.length);

        // Helper function to convert timestamp to number for consistent sorting
        const getTimestamp = (item) => {
            const timestamp = item.lastUpdated;
            // If it's a string (ISO date), convert to number
            if (typeof timestamp === 'string') {
                return new Date(timestamp).getTime();
            }
            // If it's already a number, return as is
            return timestamp || 0;
        };

        // Apply active sort (RECENT or FAVOURITES)
        if (sortBy === 'RECENT') {
            combined.sort((a, b) => {
                const aTime = getTimestamp(a);
                const bTime = getTimestamp(b);
                return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
            });
        } else if (sortBy === 'FAVOURITES') {
            // Sort by favourites first, then by lastUpdated
            const favourites = combined
                .filter((item) => {
                    if (item.type === 'whiteboard') return whiteboardFavouriteIds.includes(item.whiteboardId);
                    if (item.type === 'customisedHandout')
                        return customisedHandoutFavouriteIds.includes(item.customisedId);
                    return false;
                })
                .sort((a, b) => {
                    const aTime = getTimestamp(a);
                    const bTime = getTimestamp(b);
                    return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
                });
            const nonFavourites = combined
                .filter((item) => {
                    if (item.type === 'whiteboard') return !whiteboardFavouriteIds.includes(item.whiteboardId);
                    if (item.type === 'customisedHandout')
                        return !customisedHandoutFavouriteIds.includes(item.customisedId);
                    return true;
                })
                .sort((a, b) => {
                    const aTime = getTimestamp(a);
                    const bTime = getTimestamp(b);
                    return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
                });
            combined = [...favourites, ...nonFavourites];
        }

        setCombinedItems(combined);

        // Apply filter based on filterType
        if (filterType === 'whiteboards') {
            combined = combined.filter((item) => item.type === 'whiteboard');
        } else if (filterType === 'customisedHandouts') {
            combined = combined.filter((item) => item.type === 'customisedHandout');
        }

        // Apply search if there's an active search term
        if (searchTerm) {
            combined = combined.filter((item) => {
                if (item.type === 'whiteboard') {
                    return (
                        item.handoutContentName?.toLowerCase().includes(searchTerm) ||
                        item.handoutAlphabeticalName?.toLowerCase().includes(searchTerm) ||
                        item.handoutDocumentTitle?.toLowerCase().includes(searchTerm) ||
                        item.notes?.toLowerCase().includes(searchTerm) ||
                        item.fileTitle?.toLowerCase().includes(searchTerm) ||
                        item.patientName?.toLowerCase().includes(searchTerm)
                    );
                } else if (item.type === 'customisedHandout') {
                    const locale = 'en-UK';
                    const contentData = item.effectiveContentData?.[locale];
                    let documentTitle = '';
                    if (contentData && Array.isArray(contentData) && contentData.length > 0) {
                        const firstSection = contentData[0];
                        if (firstSection && firstSection.blocks) {
                            const titleBlock = firstSection.blocks.find((block) => block.type === 'documentTitle');
                            documentTitle = titleBlock?.value || '';
                        }
                    }
                    return (
                        documentTitle.toLowerCase().includes(searchTerm) ||
                        item.notes?.toLowerCase().includes(searchTerm)
                    );
                }
                return false;
            });
        }

        console.log('Filtered items:', combined.length, 'filterType:', filterType, 'searchTerm:', searchTerm);
        setFilteredWhiteboards(combined);
        setPage(0); // Reset to first page when filter changes
    }, [
        whiteboards,
        customisedHandouts,
        filterType,
        sortBy,
        sortDirection,
        searchTerm,
        userDataStore.userData.whiteboardsFavourites,
        userDataStore.userData.customisedHandoutsFavourites,
    ]);

    useEffect(() => {
        if (!whiteboardStore.isInitialized || paginatedWhiteboards.length === 0) return;

        let isMounted = true;
        const fetchImagesForItems = async () => {
            const newImageMap = {};
            let shouldUpdateState = false;

            for (const item of paginatedWhiteboards) {
                if (item.type === 'whiteboard') {
                    // Handle whiteboard images
                    if (!item.canvasImageLink || fetchedImages.current.has(item.canvasImageLink)) {
                        continue;
                    }
                    try {
                        const imageData = await handleFetchWhiteboardImage(item.canvasImageLink);
                        if (imageData && isMounted) {
                            newImageMap[item.canvasImageLink] = imageData;
                            fetchedImages.current.add(item.canvasImageLink);
                            shouldUpdateState = true;
                        }
                    } catch (error) {
                        console.error(`Error fetching whiteboard image: ${item.canvasImageLink}`, error);
                    }
                } else if (item.type === 'customisedHandout') {
                    // Handle customised handout images
                    // Extract illustration from effectiveContentData
                    const illustrationSrc = getCustomisedHandoutImageSrc(item);

                    if (!illustrationSrc || fetchedImages.current.has(illustrationSrc)) {
                        continue;
                    }

                    try {
                        const imageData = await handleFetchHandoutIllustration(illustrationSrc);
                        if (imageData && isMounted) {
                            newImageMap[illustrationSrc] = imageData;
                            fetchedImages.current.add(illustrationSrc);
                            shouldUpdateState = true;
                        }
                    } catch (error) {
                        console.error(`Error fetching customised handout image: ${illustrationSrc}`, error);
                    }
                }
            }

            if (isMounted && shouldUpdateState) {
                setImageMap((prev) => ({ ...prev, ...newImageMap }));
            }
        };

        fetchImagesForItems();
        return () => {
            isMounted = false;
        };
    }, [paginatedWhiteboards, whiteboardStore.isInitialized]);

    const handleSearchInputChange = (event) => {
        const value = event.target.value.toLowerCase();
        setSearchTerm(value);

        // If search is empty, reset to the filtered combined items (respects current sort and filter)
        if (value === '') {
            // Apply filter based on filterType
            let filtered = combinedItems;
            if (filterType === 'whiteboards') {
                filtered = combinedItems.filter((item) => item.type === 'whiteboard');
            } else if (filterType === 'customisedHandouts') {
                filtered = combinedItems.filter((item) => item.type === 'customisedHandout');
            }
            setFilteredWhiteboards(filtered);
            setPage(0);
            return;
        }

        // First apply the filterType (whiteboards/customisedHandouts/all)
        let itemsToSearch = combinedItems;
        if (filterType === 'whiteboards') {
            itemsToSearch = combinedItems.filter((item) => item.type === 'whiteboard');
        } else if (filterType === 'customisedHandouts') {
            itemsToSearch = combinedItems.filter((item) => item.type === 'customisedHandout');
        }

        // Then apply the search within the filtered items
        const filtered = itemsToSearch.filter((item) => {
            if (item.type === 'whiteboard') {
                return (
                    item.handoutContentName?.toLowerCase().includes(value) ||
                    item.handoutAlphabeticalName?.toLowerCase().includes(value) ||
                    item.handoutDocumentTitle?.toLowerCase().includes(value) ||
                    item.notes?.toLowerCase().includes(value) ||
                    item.fileTitle?.toLowerCase().includes(value) ||
                    item.patientName?.toLowerCase().includes(value)
                );
            } else if (item.type === 'customisedHandout') {
                // Extract document title from effectiveContentData for search
                const locale = 'en-UK';
                const contentData = item.effectiveContentData?.[locale];
                let documentTitle = '';
                if (contentData && Array.isArray(contentData) && contentData.length > 0) {
                    const firstSection = contentData[0];
                    if (firstSection && firstSection.blocks) {
                        const titleBlock = firstSection.blocks.find((block) => block.type === 'documentTitle');
                        documentTitle = titleBlock?.value || '';
                    }
                }
                return documentTitle.toLowerCase().includes(value) || item.notes?.toLowerCase().includes(value);
            }
            return false;
        });

        setFilteredWhiteboards(filtered);
        setPage(0);
    };

    const handleNextPage = () => {
        if ((page + 1) * whiteboardsPerPage < filteredWhiteboards.length) {
            setPage(page + 1);
        }
    };

    const handlePrevPage = () => {
        if (page > 0) {
            setPage(page - 1);
        }
    };

    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    // close menu in sorting button
    const handleCloseMenu = (option) => {
        if (option === 'RECENT') {
            const newDirection = sortDirection === 'desc' ? 'asc' : 'desc';
            setSortDirection(newDirection);
            setHasSortedRecently(true);
            setSortBy('RECENT');
            setPage(0);
        }

        if (option === 'FAVOURITES') {
            const isSameSort = sortBy === 'FAVOURITES';
            const newDirection = isSameSort && sortDirection === 'desc' ? 'asc' : 'desc';

            setSortDirection(newDirection);
            setHasSortedRecently(false);
            setSortBy('FAVOURITES');
            setPage(0);
        }

        setAnchorEl(null);
    };

    const handleFetchWhiteboardImage = async (canvasImageLink) => {
        if (!canvasImageLink) return null; // Prevent IndexedDB errors

        try {
            // Check if already loaded in state
            if (imageMap[canvasImageLink]) {
                return imageMap[canvasImageLink];
            }

            // Check if image exists in IndexedDB
            const imageObject = await imageStore.getImageFromIndexedDB(canvasImageLink);
            if (imageObject) return imageObject.data; // Return base64 image

            // Generate presigned URL
            const payload = {
                files: [{ method: 'get', key: canvasImageLink }],
                contentCategory: 'whiteboard',
            };
            const resp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
            const presignedUrl = resp.getUrls[0].presignedUrl;

            // Download Image
            const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);

            // Store in IndexedDB
            await imageStore.saveImageToIndexedDB(canvasImageLink, downloadResult.base64data, downloadResult.eTag);

            return downloadResult.base64data;
        } catch (error) {
            console.error(`Error fetching whiteboard image ${canvasImageLink}:`, error);
            return null;
        }
    };

    const getCustomisedHandoutImageSrc = (customisedHandout) => {
        // First check if there's an attached whiteboard with canvas image link
        if (customisedHandout.attachedWhiteboardCanvasImageLink) {
            return customisedHandout.attachedWhiteboardCanvasImageLink;
        }

        // Extract first illustration from effectiveContentData
        const locale = 'en-UK';
        const contentData = customisedHandout.effectiveContentData?.[locale];
        if (!contentData || !Array.isArray(contentData)) {
            return null;
        }

        // Find first illustration block
        for (const section of contentData) {
            if (section.blocks) {
                const illustrationBlock = section.blocks.find((block) => block.type === 'illustration');
                if (illustrationBlock?.src) {
                    return illustrationBlock.src;
                }
            }
        }

        return null;
    };

    const handleFetchHandoutIllustration = async (illustrationSrc) => {
        if (!illustrationSrc) return null;

        try {
            // Check if already loaded in state
            if (imageMap[illustrationSrc]) {
                return imageMap[illustrationSrc];
            }

            // Check if image exists in IndexedDB
            const imageObject = await imageStore.getImageFromIndexedDB(illustrationSrc);
            if (imageObject) return imageObject.data;

            // Detect if this is a whiteboard image path (starts with 'png/')
            const isWhiteboardImage = illustrationSrc.startsWith('png/');

            // Generate presigned URL with appropriate content category
            const payload = {
                files: [{ method: 'get', key: illustrationSrc }],
                contentCategory: isWhiteboardImage ? 'whiteboard' : 'image',
            };
            const resp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
            const presignedUrl = resp.getUrls[0].presignedUrl;

            // Download Image
            const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);

            // Store in IndexedDB
            await imageStore.saveImageToIndexedDB(illustrationSrc, downloadResult.base64data, downloadResult.eTag);

            return downloadResult.base64data;
        } catch (error) {
            console.error(`Error fetching handout illustration ${illustrationSrc}:`, error);
            return null;
        }
    };

    const handleArchiveWhiteboard = (wb) =>
        archiveWhiteboard(
            wb,
            userDataStore,
            whiteboardStore,
            authStore,
            setWhiteboards,
            setFilteredWhiteboards,
            showSnackbar,
        );

    const handleToggleFavourite = async (whiteboardId) => {
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
        const isFavourite = userDataStore.userData.whiteboardsFavourites?.includes(whiteboardId);
        const updatedFavourites = isFavourite
            ? userDataStore.userData.whiteboardsFavourites.filter((id) => id !== whiteboardId)
            : [...(userDataStore.userData.whiteboardsFavourites || []), whiteboardId];

        if (!isFavourite && userDataStore.userData.whiteboardsFavourites.length >= 30) {
            showSnackbar('You can only mark up to 30 whiteboards as favourites.', 'warning');
            return;
        }

        // Update local store
        userDataStore.setUserData({
            ...userDataStore.userData,
            whiteboardsFavourites: updatedFavourites,
        });

        // Send update to backend
        const payload = {
            whiteboardsFavourites: updatedFavourites,
        };

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
        } catch (err) {
            console.error('Failed to update favourites', err);
        }
    };

    const handleEditWhiteboard = (whiteboard) => {
        if (!whiteboard.handoutId) {
            console.error('Missing handoutContentId for whiteboard:', whiteboard);
            return;
        }

        // Set whiteboard state in localStorage so it's loaded on Handout page
        localStorage.setItem(
            'lastWhiteboardState',
            JSON.stringify({
                whiteboardId: whiteboard.whiteboardId,
                handoutId: whiteboard.handoutId,
                canvasImageLink: whiteboard.canvasImageLink,
                canvasJsonLink: whiteboard.canvasJsonLink,
                fileName: whiteboard.fileName || '',
                eTag: whiteboard.eTag,
                ...(whiteboard.lastUsedColours && { lastUsedColours: whiteboard.lastUsedColours }),
                ...(whiteboard.updatedByFirstName && {
                    updatedByFirstName: whiteboard.updatedByFirstName,
                }),
                ...(whiteboard.updatedByLastName && {
                    updatedByLastName: whiteboard.updatedByLastName,
                }),
                ...(whiteboard.diagramVariant && { diagramVariant: whiteboard.diagramVariant }),
            }),
        );

        sessionStorage.setItem('autoloadWhiteboardFromLibrary', 'true');
        // pass details for disclaimer
        sessionStorage.setItem(
            'whiteboardDoctorName',
            `${whiteboard.updatedByFirstName || ''} ${whiteboard.updatedByLastName || ''}`.trim(),
        );
        sessionStorage.setItem('whiteboardClinicName', userDataStore?.userData?.clinicName || '');
        // Navigate to the handout page
        navigate(`/handout/${whiteboard.handoutId}`);
    };

    // Customised Handout Handlers
    const handleToggleFavouriteCustomisedHandout = async (customisedId) => {
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
        const isFavourite = userDataStore.userData.customisedHandoutsFavourites?.includes(customisedId);
        const updatedFavourites = isFavourite
            ? userDataStore.userData.customisedHandoutsFavourites.filter((id) => id !== customisedId)
            : [...(userDataStore.userData.customisedHandoutsFavourites || []), customisedId];

        if (!isFavourite && (userDataStore.userData.customisedHandoutsFavourites?.length || 0) >= 30) {
            showSnackbar('You can only mark up to 30 customised handouts as favourites.', 'warning');
            return;
        }

        // Update local store
        userDataStore.setUserData({
            ...userDataStore.userData,
            customisedHandoutsFavourites: updatedFavourites,
        });

        // Send update to backend
        const payload = {
            customisedHandoutsFavourites: updatedFavourites,
        };

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
        } catch (err) {
            console.error('Failed to update customised handout favourites', err);
        }
    };

    const handleEditCustomisedHandout = (customisedHandout) => {
        if (!customisedHandout.baseContentId) {
            console.error('Missing baseContentId for customised handout:', customisedHandout);
            showSnackbar('Cannot edit: missing base content ID', 'error');
            return;
        }

        // CRITICAL: Clear any whiteboard state BEFORE setting customised handout flag
        // This prevents race condition where both whiteboard and CH auto-load effects fire
        localStorage.removeItem('lastWhiteboardState');

        // Store CH ID in localStorage (similar to whiteboard pattern)
        localStorage.setItem('loadCustomisedHandout', customisedHandout.customisedId);

        console.log('Navigating to edit customised handout:', customisedHandout.customisedId);

        // Navigate to base handout
        navigate(`/handout/${customisedHandout.baseContentId}`);
    };

    const handleArchiveCustomisedHandout = (customisedHandout) =>
        archiveCustomisedHandout(
            customisedHandout,
            userDataStore,
            customisedHandoutStore,
            authStore,
            setCustomisedHandouts,
            setFilteredWhiteboards,
            showSnackbar,
        );

    // **Prevent rendering until store is ready**
    if (!whiteboardStore.isInitialized) {
        return (
            <Typography variant="h6" align="center">
                Loading Whiteboards...
            </Typography>
        );
    }

    return (
        <>
            <GradientBanner
                title="My Library"
                subtitle="Explore, edit and share your saved whiteboards and customised handouts"
                placeholder="Search..."
                searchTerm={searchTerm}
                onSearchChange={handleSearchInputChange}
                isMobile={isMobile}
                isTablet={isTablet}
                height={isMobile ? 'initial' : '450px'}
                paddingY={isMobile ? '48px' : '40px'}
                paddingBottom="40px"
                className="search-banner"
            />

            {/* Main Panel */}
            <Container
                component={'section'}
                maxWidth={'xl'}
                sx={{ backgroundColor: '#F0F0FC', px: isMobileOrTablet ? 2 : 6, pt: 3 }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent:
                            userDataStore?.subscriptionData?.accountType === 'Student' ? 'flex-end' : 'space-between',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        mt: 2,
                        pr: isMobile ? 0 : 2,
                        mb: isMobile ? 2 : '67px',
                        paddingX: isMobile ? '48px' : '66px',
                        gap: isMobile ? 1.5 : 2,
                    }}
                >
                    {/* Filter Radio Buttons - Hidden for Student plan */}
                    {userDataStore?.subscriptionData?.accountType !== 'Student' && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: isMobile ? 1.5 : 3,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                width: isMobile ? '100%' : 'auto',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: 'Inter',
                                    fontWeight: 600,
                                    fontSize: isMobile ? '12px' : '14px',
                                    lineHeight: '14px',
                                    letterSpacing: '2px',
                                    textTransform: 'uppercase',
                                    color: '#1919194D',
                                    flexShrink: 0,
                                }}
                            >
                                SHOW:
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: isMobile ? 1.5 : 2,
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box
                                    onClick={() => setFilterType('all')}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: isMobile ? 14 : 16,
                                            height: isMobile ? 14 : 16,
                                            borderRadius: '50%',
                                            border: '2px solid #000453',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: filterType === 'all' ? '#000453' : 'transparent',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {filterType === 'all' && (
                                            <Box
                                                sx={{
                                                    width: isMobile ? 6 : 8,
                                                    height: isMobile ? 6 : 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: 'white',
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter',
                                            fontSize: isMobile ? '12px' : '14px',
                                            color: filterType === 'all' ? '#000453' : '#1919194D',
                                            fontWeight: filterType === 'all' ? 600 : 500,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        All
                                    </Typography>
                                </Box>

                                <Box
                                    onClick={() => setFilterType('whiteboards')}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: isMobile ? 14 : 16,
                                            height: isMobile ? 14 : 16,
                                            borderRadius: '50%',
                                            border: '2px solid #000453',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: filterType === 'whiteboards' ? '#000453' : 'transparent',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {filterType === 'whiteboards' && (
                                            <Box
                                                sx={{
                                                    width: isMobile ? 6 : 8,
                                                    height: isMobile ? 6 : 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: 'white',
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter',
                                            fontSize: isMobile ? '12px' : '14px',
                                            color: filterType === 'whiteboards' ? '#000453' : '#1919194D',
                                            fontWeight: filterType === 'whiteboards' ? 600 : 500,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Whiteboards
                                    </Typography>
                                </Box>

                                <Box
                                    onClick={() => setFilterType('customisedHandouts')}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: isMobile ? 14 : 16,
                                            height: isMobile ? 14 : 16,
                                            borderRadius: '50%',
                                            border: '2px solid #000453',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor:
                                                filterType === 'customisedHandouts' ? '#000453' : 'transparent',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {filterType === 'customisedHandouts' && (
                                            <Box
                                                sx={{
                                                    width: isMobile ? 6 : 8,
                                                    height: isMobile ? 6 : 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: 'white',
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter',
                                            fontSize: isMobile ? '12px' : '14px',
                                            color: filterType === 'customisedHandouts' ? '#000453' : '#1919194D',
                                            fontWeight: filterType === 'customisedHandouts' ? 600 : 500,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Customised Handouts
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Sort By Section */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: isMobile ? '100%' : 'auto',
                            justifyContent: isMobile ? 'flex-start' : 'flex-end',
                        }}
                    >
                        {/* Sort By Text */}
                        <Typography
                            onClick={handleOpenMenu}
                            sx={{
                                fontFamily: 'Inter',
                                fontWeight: 600,
                                fontSize: isMobile ? '12px' : '14px',
                                lineHeight: '14px',
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                                color: '#1919194D',
                                cursor: 'pointer',
                            }}
                        >
                            SORT BY{' '}
                            <Typography
                                component="span"
                                sx={{
                                    color: '#000453',
                                }}
                            >
                                {sortBy}
                            </Typography>
                        </Typography>

                        {/* Dropdown Icon */}
                        <IconButton onClick={handleOpenMenu} sx={{ ml: 1, p: 0 }}>
                            {sortBy === 'RECENT' && hasSortedRecently ? (
                                sortDirection === 'desc' ? (
                                    <ExpandMoreIcon sx={{ color: '#000453', fontSize: isMobile ? '20px' : '24px' }} />
                                ) : (
                                    <ExpandLessIcon sx={{ color: '#000453', fontSize: isMobile ? '20px' : '24px' }} />
                                )
                            ) : (
                                <ExpandMoreIcon sx={{ color: '#000453', fontSize: isMobile ? '20px' : '24px' }} />
                            )}
                        </IconButton>
                    </Box>

                    {/* Dropdown Menu */}
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleCloseMenu(null)}>
                        <MenuItem onClick={() => handleCloseMenu('RECENT')} sx={{ color: '#000453' }}>
                            RECENT
                        </MenuItem>
                        <MenuItem onClick={() => handleCloseMenu('FAVOURITES')} sx={{ color: '#000453' }}>
                            FAVOURITES
                        </MenuItem>
                    </Menu>
                </Box>

                {filteredWhiteboards.length === 0 ? (
                    <Box sx={{ mt: 6, textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ color: '#000453' }}>
                            No items available yet.
                        </Typography>

                        <Typography variant="body1" sx={{ mt: 1, color: '#000453' }}>
                            To start a whiteboard session, select your handout of choice and then toggle on whiteboard.
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#000453' }}>
                            Annotate our illustration and then click save. Once saved your whiteboard will appear here.
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2, color: '#000453' }}>
                            To create a customised handout, open any handout and toggle on Customise mode.
                        </Typography>
                        {/* Button aligned to the right, text unchanged */}
                        <Box
                            sx={{
                                mt: 3,
                                mb: 3,
                                display: 'flex',
                                justifyContent: { xs: 'center', sm: 'flex-end' },
                            }}
                        >
                            <PinkGradientButtonWithArrow label="Search Handouts" onClick={() => navigate('/search')} />
                        </Box>
                    </Box>
                ) : (
                    <Grid
                        container
                        spacing={3}
                        px={isMobile ? '48px' : '66px'}
                        sx={{ position: 'relative', ml: '0px!important', width: '100%' }}
                    >
                        {paginatedWhiteboards.map((item, index) => {
                            if (item.type === 'whiteboard') {
                                return (
                                    <WhiteboardCard
                                        key={item.whiteboardId || index}
                                        whiteboard={item}
                                        isMobile={isMobile}
                                        userDataStore={userDataStore}
                                        authStore={authStore}
                                        whiteboardStore={whiteboardStore}
                                        showSnackbar={showSnackbar}
                                        handleToggleFavourite={handleToggleFavourite}
                                        handleEditWhiteboard={handleEditWhiteboard}
                                        handleArchiveWhiteboard={handleArchiveWhiteboard}
                                        imageMap={imageMap}
                                        index={index}
                                    />
                                );
                            } else if (item.type === 'customisedHandout') {
                                return (
                                    <CustomisedHandoutCard
                                        key={item.customisedId || index}
                                        customisedHandout={item}
                                        isMobile={isMobile}
                                        userDataStore={userDataStore}
                                        showSnackbar={showSnackbar}
                                        handleToggleFavourite={handleToggleFavouriteCustomisedHandout}
                                        handleEditCustomisedHandout={handleEditCustomisedHandout}
                                        handleArchiveCustomisedHandout={handleArchiveCustomisedHandout}
                                        imageMap={imageMap}
                                        index={index}
                                    />
                                );
                            }
                            return null;
                        })}

                        {/* Pagination Controls */}
                        {combinedItems.length > 6 && (
                            <>
                                <IconButton
                                    onClick={handlePrevPage}
                                    disabled={page === 0}
                                    sx={{
                                        position: 'absolute',
                                        left: '-10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 10,
                                        backgroundColor: 'rgba(0, 4, 83, 0.08)',
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 4, 83, 0.12)',
                                        },
                                    }}
                                >
                                    <ChevronLeftRoundedIcon sx={{ fontSize: '32px', color: '#000453' }} />
                                </IconButton>

                                <IconButton
                                    onClick={handleNextPage}
                                    disabled={(page + 1) * whiteboardsPerPage >= filteredWhiteboards.length}
                                    sx={{
                                        position: 'absolute',
                                        right: '-10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 10,
                                        backgroundColor: 'rgba(0, 4, 83, 0.08)',
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 4, 83, 0.12)',
                                        },
                                    }}
                                >
                                    <ChevronRightRoundedIcon sx={{ fontSize: '32px', color: '#000453' }} />
                                </IconButton>
                            </>
                        )}
                    </Grid>
                )}
            </Container>

            {/* New Feedback Section */}
            <FeedbackCTA />
        </>
    );
};

export default MyLibrary;
